import * as React from 'react'
import { CenterFlexbox } from '../../../../common/src/components/core'
import Canvas from './Canvas'

export default class WaveAnimation extends React.PureComponent<
  Record<string, never>,
  {
    /**
     * Perlin noise generator with vector math functions
     */
    perlin: ClassicalNoise
    /**
     * Array of "speed variations" we can apply to each wave.
     * By varying the speed in a loop, we cause a rhythmic acceleration and deceleration.
     */
    variators: number[]
  }
> {
  AMPLITUDE = 200
  MAX_LINES = 6
  POINTS_PER_LINE = 200
  VARIATION = 0.004
  U_WIDTH = 0.4

  constructor(props: Record<string, never> | Readonly<Record<string, never>>) {
    super(props)

    const v: number[] = []
    for (let i = 0, u = 1; i < this.MAX_LINES; i++, u += this.U_WIDTH / this.MAX_LINES) {
      v[i] = u
    }

    this.state = {
      perlin: new ClassicalNoise({}),
      variators: v,
    }
  }

  /**
   * Frame-by-frame, what we draw on the canvas.
   *
   * `frameCount` is a required prop: Canvas uses it with requestAnimationFrame to hit 60fps.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  draw = (ctx: CanvasRenderingContext2D | null, frameCount: number) => {
    console.log('Starting wave...')

    if (ctx != null) {
      const width = ctx.canvas.width
      const height = ctx.canvas.height

      // clear frame
      ctx.clearRect(0, 0, width, height)

      // draw the waves
      const xStep: number = Math.ceil(width / this.POINTS_PER_LINE)
      ctx.shadowColor = 'rgba(10, 241, 255, 1)'
      ctx.shadowBlur = 10

      for (let i = 0; i <= this.MAX_LINES; i++) {
        ctx.beginPath()
        ctx.moveTo(0, height / 2)
        let y = 0
        for (let x = 0; x <= width; x += xStep) {
          y = this.state.perlin.noise(x * this.VARIATION + this.state.variators[i], x * this.VARIATION, 0)
          ctx.lineTo(x, height / 2 + this.AMPLITUDE * y)
        }
        // uncomment to add new colors to wave
        // var color = Math.floor(150 * Math.abs(y))

        const alpha = Math.min(0.1 + Math.abs(y), 0.8) + 0.1
        ctx.strokeStyle = 'rgba(80,210,240,' + alpha + ')'
        ctx.stroke()
        ctx.closePath()

        this.setState(prevState => ({
          variators: this.mutateVariatorElem(prevState.variators, i),
        }))
      }

      // end of loop, start over
      return undefined
    }

    console.error("Canvas element is either full or can't accept 2D renders at the moment.")
    return null
  }

  /**
   * React needs to update "variators" state in a function
   * ... which is a little silly, but beats fighting linter errors
   *
   * @param variators from this.state: an array we can't modify directly — we can only replace it
   * @param index of the element to permute
   */
  // eslint-disable-next-line arrow-body-style
  mutateVariatorElem = (variators: number[], index: number) => {
    return variators.map((val: number, i) => (i === index ? val + 0.005 : val))
  }

  render() {
    return (
      <CenterFlexbox style={{ height: '100%' }}>
        <Canvas draw={this.draw} style={{ width: '100%', height: '600px' }} />
      </CenterFlexbox>
    )
  }
}

/**
 * Classic Perlin noise with handy vector math functions
 */
class ClassicalNoise extends React.PureComponent<unknown, { grad3: any[]; p: number[]; perm: number[] }> {
  constructor(props: any) {
    super(props)

    const p = []
    for (let i = 0; i < 256; i++) {
      p[i] = Math.floor(Math.random() * 256)
    }

    // To remove the need for index wrapping, double the permutation table length
    const perm = []
    for (let j = 0; j < 512; j++) {
      perm[j] = p[j & 255]
    }

    this.state = {
      grad3: [
        [1, 1, 0],
        [-1, 1, 0],
        [1, -1, 0],
        [-1, -1, 0],
        [1, 0, 1],
        [-1, 0, 1],
        [1, 0, -1],
        [-1, 0, -1],
        [0, 1, 1],
        [0, -1, 1],
        [0, 1, -1],
        [0, -1, -1],
      ],
      p,
      perm,
    }
  }

  // render() {
  //   return this
  // }

  dot(g: any, x: number, y: number, z: number): number {
    return g[0] * x + g[1] * y + g[2] * z
  }

  mix(a: number, b: number, t: number): number {
    return (1 - t) * a + t * b
  }

  fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10)
  }

  // Classic Perlin noise, 3D version
  noise(x: number, y: number, z: number): number {
    // Find unit grid cell containing point
    let X = Math.floor(x)
    let Y = Math.floor(y)
    let Z = Math.floor(z)

    // Get relative xyz coordinates of point within that cell
    x = x - X
    y = y - Y
    z = z - Z

    // Wrap the integer cells at 255 (smaller integer period can be introduced here)
    X = X & 255
    Y = Y & 255
    Z = Z & 255

    // Calculate a set of eight hashed gradient indices
    const gi000 = this.state.perm[X + this.state.perm[Y + this.state.perm[Z]]] % 12
    const gi001 = this.state.perm[X + this.state.perm[Y + this.state.perm[Z + 1]]] % 12
    const gi010 = this.state.perm[X + this.state.perm[Y + 1 + this.state.perm[Z]]] % 12
    const gi011 = this.state.perm[X + this.state.perm[Y + 1 + this.state.perm[Z + 1]]] % 12
    const gi100 = this.state.perm[X + 1 + this.state.perm[Y + this.state.perm[Z]]] % 12
    const gi101 = this.state.perm[X + 1 + this.state.perm[Y + this.state.perm[Z + 1]]] % 12
    const gi110 = this.state.perm[X + 1 + this.state.perm[Y + 1 + this.state.perm[Z]]] % 12
    const gi111 = this.state.perm[X + 1 + this.state.perm[Y + 1 + this.state.perm[Z + 1]]] % 12

    // The gradients of each corner are now:
    // g000 = grad3[gi000];
    // g001 = grad3[gi001];
    // g010 = grad3[gi010];
    // g011 = grad3[gi011];
    // g100 = grad3[gi100];
    // g101 = grad3[gi101];
    // g110 = grad3[gi110];
    // g111 = grad3[gi111];
    // Calculate noise contributions from each of the eight corners
    const n000 = this.dot(this.state.grad3[gi000], x, y, z)
    const n100 = this.dot(this.state.grad3[gi100], x - 1, y, z)
    const n010 = this.dot(this.state.grad3[gi010], x, y - 1, z)
    const n110 = this.dot(this.state.grad3[gi110], x - 1, y - 1, z)
    const n001 = this.dot(this.state.grad3[gi001], x, y, z - 1)
    const n101 = this.dot(this.state.grad3[gi101], x - 1, y, z - 1)
    const n011 = this.dot(this.state.grad3[gi011], x, y - 1, z - 1)
    const n111 = this.dot(this.state.grad3[gi111], x - 1, y - 1, z - 1)
    // Compute the fade curve value for each of x, y, z
    const u = this.fade(x)
    const v = this.fade(y)
    const w = this.fade(z)
    // Interpolate along x the contributions from each of the corners
    const nx00 = this.mix(n000, n100, u)
    const nx01 = this.mix(n001, n101, u)
    const nx10 = this.mix(n010, n110, u)
    const nx11 = this.mix(n011, n111, u)
    // Interpolate the four results along y
    const nxy0 = this.mix(nx00, nx10, v)
    const nxy1 = this.mix(nx01, nx11, v)

    // Interpolate the two last results along z
    return this.mix(nxy0, nxy1, w)
  }
}
